import mapboxgl from "mapbox-gl";
import * as Sentry from "@sentry/react";
import {
  readStorageItem,
  removeStorageItem,
  writeStorageItem,
} from "./safeStorage";

interface CachedGeocodingResult {
  data: any;
  timestamp: number;
}

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class MapboxManager {
  private static instance: MapboxManager;
  private maps: Map<string, mapboxgl.Map>;
  private accessToken: string | undefined;
  private lastUsedTimestamps: Map<string, number>;
  private geocodingCache: Map<string, CachedGeocodingResult>;
  private pendingRequests: Map<string, PendingRequest>;
  private readonly CLEANUP_INTERVAL = 1000 * 60 * 30; // 30 minutes
  private readonly MAX_IDLE_TIME = 1000 * 60 * 60; // 1 hour
  private readonly GEOCODING_CACHE_DURATION = 1000 * 60 * 60 * 24 * 30; // 30 days
  private readonly COORDINATE_PRECISION = 0.001; // ~100m precision for caching

  private constructor() {
    this.maps = new Map();
    this.lastUsedTimestamps = new Map();
    this.geocodingCache = new Map();
    this.pendingRequests = new Map();
    this.accessToken = import.meta.env["VITE_MAPBOX_ACCESS_TOKEN"];

    if (!this.accessToken) {
      console.error(
        "Mapbox access token is missing. Please check your .env file"
      );
    } else {
      mapboxgl.accessToken = this.accessToken;
    }

    // Load cached geocoding results from localStorage
    this.loadGeocodingCache();

    // Start cleanup intervals
    setInterval(() => this.cleanupUnusedMaps(), this.CLEANUP_INTERVAL);
    setInterval(() => this.cleanupExpiredCache(), this.CLEANUP_INTERVAL);
    setInterval(() => this.cleanupPendingRequests(), this.CLEANUP_INTERVAL);
  }

  public static getInstance(): MapboxManager {
    if (!MapboxManager.instance) {
      MapboxManager.instance = new MapboxManager();
    }
    return MapboxManager.instance;
  }

  public async getMap(
    containerId: string,
    options: Omit<mapboxgl.MapOptions, "container">,
    forceNew = false
  ): Promise<mapboxgl.Map> {
    // If we already have a map for this container and don't want to force new
    if (!forceNew && this.maps.has(containerId)) {
      const existingMap = this.maps.get(containerId)!;
      this.updateLastUsed(containerId);
      return existingMap;
    }

    // Clean up existing map if it exists
    await this.removeMap(containerId);

    // Create new map
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with id ${containerId} not found`);
    }

    const map = new mapboxgl.Map({
      container: containerId,
      ...options,
    });

    // Wait for the map to load
    await new Promise<void>((resolve, reject) => {
      map.once("load", () => resolve());
      map.once("error", (e) => reject(e));
    });

    this.maps.set(containerId, map);
    this.updateLastUsed(containerId);

    return map;
  }

  public async removeMap(containerId: string): Promise<void> {
    const existingMap = this.maps.get(containerId);
    if (existingMap) {
      existingMap.remove();
      this.maps.delete(containerId);
      this.lastUsedTimestamps.delete(containerId);
    }
  }

  private updateLastUsed(containerId: string): void {
    this.lastUsedTimestamps.set(containerId, Date.now());
  }

  private async cleanupUnusedMaps(): Promise<void> {
    const now = Date.now();
    for (const [containerId, lastUsed] of this.lastUsedTimestamps.entries()) {
      if (now - lastUsed > this.MAX_IDLE_TIME) {
        await this.removeMap(containerId);
      }
    }
  }

  public getAccessToken(): string | undefined {
    return this.accessToken;
  }

  private generateCacheKey(longitude: number, latitude: number): string {
    // Round coordinates to reduce cache size and increase hit rate
    const roundedLng =
      Math.round(longitude / this.COORDINATE_PRECISION) *
      this.COORDINATE_PRECISION;
    const roundedLat =
      Math.round(latitude / this.COORDINATE_PRECISION) *
      this.COORDINATE_PRECISION;
    return `${roundedLng},${roundedLat}`;
  }

  private loadGeocodingCache(): void {
    try {
      const cached = readStorageItem("mapbox_geocoding_cache");
      if (cached) {
        const parsedCache = JSON.parse(cached);
        this.geocodingCache = new Map(Object.entries(parsedCache));
        console.log(
          `Loaded ${this.geocodingCache.size} cached geocoding results`
        );
      }
    } catch (error) {
      console.error("Error loading geocoding cache:", error);
      this.geocodingCache = new Map();
      removeStorageItem("mapbox_geocoding_cache");
    }
  }

  private saveGeocodingCache(): void {
    try {
      const cacheObject = Object.fromEntries(this.geocodingCache);
      writeStorageItem("mapbox_geocoding_cache", JSON.stringify(cacheObject));
    } catch (error) {
      console.error("Error saving geocoding cache:", error);
    }
  }

  private cleanupExpiredCache(): void {
    const now = Date.now();
    let removedCount = 0;

    for (const [key, cached] of this.geocodingCache.entries()) {
      if (now - cached.timestamp > this.GEOCODING_CACHE_DURATION) {
        this.geocodingCache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      console.log(`Cleaned up ${removedCount} expired geocoding cache entries`);
      this.saveGeocodingCache();
    }
  }

  private cleanupPendingRequests(): void {
    const now = Date.now();
    const maxPendingTime = 30000; // 30 seconds

    for (const [key, pending] of this.pendingRequests.entries()) {
      if (now - pending.timestamp > maxPendingTime) {
        this.pendingRequests.delete(key);
      }
    }
  }

  public async reverseGeocode(
    longitude: number,
    latitude: number
  ): Promise<any> {
    const cacheKey = this.generateCacheKey(longitude, latitude);

    // Check cache first
    const cached = this.geocodingCache.get(cacheKey);
    if (
      cached &&
      Date.now() - cached.timestamp < this.GEOCODING_CACHE_DURATION
    ) {
      console.log("Using cached geocoding result for", cacheKey);
      return cached.data;
    }

    // Check if request is already pending to avoid duplicates
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      console.log("Using pending geocoding request for", cacheKey);
      return pending.promise;
    }

    // Make new request
    console.log("Making new geocoding request for", cacheKey);
    const requestPromise = this.makeGeocodingRequest(longitude, latitude);

    // Store pending request
    this.pendingRequests.set(cacheKey, {
      promise: requestPromise,
      timestamp: Date.now(),
    });

    try {
      const result = await requestPromise;

      // Cache the result
      this.geocodingCache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      // Save to localStorage
      this.saveGeocodingCache();

      return result;
    } finally {
      // Remove from pending requests
      this.pendingRequests.delete(cacheKey);
    }
  }

  private async makeGeocodingRequest(
    longitude: number,
    latitude: number
  ): Promise<any> {
    try {
      const response = await fetch(
        `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${longitude}&latitude=${latitude}&access_token=${this.accessToken}`
      );

      if (!response.ok) {
        const error = new Error(
          `Geocoding request failed: ${response.status} ${response.statusText}`
        );
        Sentry.captureException(error, {
          level: "warning",
          tags: {
            operation: "mapbox.reverse-geocode",
            provider: "mapbox",
            handled: "true",
            status: String(response.status),
          },
          extra: {
            statusText: response.statusText,
          },
        });
        throw error;
      }

      return response.json();
    } catch (error) {
      if (
        error instanceof Error &&
        !error.message.startsWith("Geocoding request failed:")
      ) {
        Sentry.captureException(error, {
          level: "warning",
          tags: {
            operation: "mapbox.reverse-geocode",
            provider: "mapbox",
            handled: "true",
            status: "network",
          },
        });
      }
      throw error;
    }
  }

  public getCacheStats(): { size: number; hitRate?: number } {
    return {
      size: this.geocodingCache.size,
      // Hit rate could be tracked with additional counters if needed
    };
  }

  public clearGeocodingCache(): void {
    this.geocodingCache.clear();
    removeStorageItem("mapbox_geocoding_cache");
    console.log("Geocoding cache cleared");
  }
}

export default MapboxManager;
